import React, { useState, useEffect } from 'react';
import { api } from '../../api.js';
import AddressAutocomplete from '../../components/AddressAutocomplete.jsx';
import ManagedDropdown from './ManagedDropdown.jsx';
import PhoneInput from '../../components/PhoneInput.jsx';

const blank = {
  name: '', category: '', contactName: '', phone: '', email: '', website: '',
  address: '', city: '', province: '', postalCode: '', country: 'Canada', status: 'Active', notes: '',
  paymentTerms: '', paymentMethod: '', currency: 'CAD', ourAccountNumber: '', creditLimit: '',
  discountPct: '', discountDays: '', taxId: '', bankName: '', transitNumber: '', institutionNumber: '', bankAccount: ''
};

function fromVendor(v) {
  if (!v) return blank;
  return {
    name: v.name || '', category: v.category || '', contactName: v.contact_name || '', phone: v.phone || '', email: v.email || '', website: v.website || '',
    address: v.address || '', city: v.city || '', province: v.province || '', postalCode: v.postal_code || '', country: v.country || 'Canada',
    status: v.status || 'Active', notes: v.notes || '',
    paymentTerms: v.payment_terms || '', paymentMethod: v.payment_method || '', currency: v.currency || 'CAD',
    ourAccountNumber: v.our_account_number || '', creditLimit: v.credit_limit ?? '',
    discountPct: v.discount_pct ?? '', discountDays: v.discount_days ?? '', taxId: v.tax_id || '',
    bankName: v.bank_name || '', transitNumber: v.transit_number || '', institutionNumber: v.institution_number || '', bankAccount: v.bank_account || ''
  };
}

const TABS = ['Basic Information', 'Payment Terms', 'Documents'];

export default function VendorFormModal({ vendor, onClose, onSaved }) {
  const [savedId, setSavedId] = useState(vendor?.id || null);
  const [form, setForm] = useState(() => fromVendor(vendor));
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [manualAddress, setManualAddress] = useState(false);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // ── Documents (tab 2) ────────────────────────────────────────────────────
  const [checklist, setChecklist] = useState(null);
  const loadChecklist = () => { if (savedId) api(`/dispatch/vendors/${savedId}/checklist`).then(setChecklist).catch(() => setChecklist([])); };
  useEffect(() => { if (tab === 2 && savedId) loadChecklist(); }, [tab, savedId]);

  const toggleChecklistItem = async (item, isChecked) => {
    await api(`/dispatch/vendors/${savedId}/checklist/${item.doc_type_id}`, { method: 'POST', body: { isChecked, expiryDate: item.expiry_date } });
    loadChecklist();
  };
  const setChecklistExpiry = async (item, expiryDate) => {
    await api(`/dispatch/vendors/${savedId}/checklist/${item.doc_type_id}`, { method: 'POST', body: { isChecked: !!item.is_checked, expiryDate } });
    loadChecklist();
  };
  const addDocType = async () => {
    const name = window.prompt('New document type name');
    if (!name || !name.trim()) return;
    await api('/dispatch/vendor-doc-types', { method: 'POST', body: { name, isRequired: false } });
    loadChecklist();
  };

  const persist = async () => {
    if (!form.name.trim()) { setError('Vendor name is required.'); return null; }
    setSaving(true);
    setError(null);
    try {
      const saved = savedId
        ? await api(`/dispatch/vendors/${savedId}`, { method: 'PATCH', body: form })
        : await api('/dispatch/vendors', { method: 'POST', body: form });
      setSavedId(saved.id);
      return saved;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveAndClose = async () => { if (await persist()) onSaved(); };
  const saveAndNext = async () => { if (await persist()) setTab(t => Math.min(t + 1, TABS.length - 1)); };
  const reset = () => setForm(vendor ? fromVendor(vendor) : blank);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal customer-modal" onClick={e => e.stopPropagation()}>
        <div className="customer-modal-title">{vendor ? 'Edit vendor' : 'New vendor'}</div>

        <div className="customer-modal-tabs">
          <div className="form-tabs" style={{ marginBottom: 0 }}>
            {TABS.map((t, i) => (
              <button key={t} type="button" className={i === tab ? 'form-tab active' : 'form-tab'} onClick={() => setTab(i)}>{t}</button>
            ))}
          </div>
        </div>

        <div className="customer-modal-body">
          {tab === 0 && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Vendor Name / Company<br/><input value={form.name} onChange={e => set('name', e.target.value)} /></label>
                <label>Category<br/>
                  <ManagedDropdown field="vendor_category" value={form.category} onChange={v => set('category', v)} placeholder="e.g. Mechanic, Parts Supplier, Tire Shop" />
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Contact Name<br/><input value={form.contactName} onChange={e => set('contactName', e.target.value)} /></label>
                <label>Phone<br/><PhoneInput value={form.phone} onChange={v => set('phone', v)} /></label>
                <label>Email<br/><input value={form.email} onChange={e => set('email', e.target.value)} /></label>
              </div>

              <label style={{ display: 'block', marginBottom: 18 }}>Website<br/><input value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://..." /></label>

              <label>Address<br/>
                <AddressAutocomplete value={form.address} onChange={val => set('address', val)}
                  onPlaceSelected={({ address, city, province, postalCode, country }) => {
                    setForm(f => ({ ...f, address, city, province, postalCode, country }));
                    setManualAddress(true);
                  }} />
              </label>
              <button type="button" className="link-toggle" style={{ marginBottom: manualAddress ? 12 : 18 }} onClick={() => setManualAddress(m => !m)}>
                {manualAddress ? 'Hide manual fields' : "Can't find it? Enter manually"}
              </button>
              {manualAddress && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                  <label>City<br/><input value={form.city} onChange={e => set('city', e.target.value)} /></label>
                  <label>Province / State<br/><input value={form.province} onChange={e => set('province', e.target.value)} /></label>
                  <label>Postal / Zip<br/><input value={form.postalCode} onChange={e => set('postalCode', e.target.value)} /></label>
                  <label>Country<br/>
                    <select value={form.country} onChange={e => set('country', e.target.value)}>
                      <option>Canada</option><option>United States</option>
                    </select>
                  </label>
                </div>
              )}

              <label style={{ display: 'block', maxWidth: 320, marginBottom: 18 }}>Status<br/>
                <select value={form.status} onChange={e => set('status', e.target.value)}>
                  <option>Active</option><option>Inactive</option>
                </select>
              </label>

              <label style={{ display: 'block' }}>Notes<br/><input value={form.notes} onChange={e => set('notes', e.target.value)} /></label>
            </>
          )}

          {tab === 1 && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Payment Terms<br/>
                  <select value={form.paymentTerms} onChange={e => set('paymentTerms', e.target.value)}>
                    <option value="">—</option>
                    <option>Due on Receipt</option><option>COD</option><option>Net 15</option><option>Net 30</option><option>Net 45</option><option>Net 60</option>
                  </select>
                </label>
                <label>Preferred Payment Method<br/>
                  <select value={form.paymentMethod} onChange={e => set('paymentMethod', e.target.value)}>
                    <option value="">—</option>
                    <option>Cheque</option><option>ACH / EFT</option><option>Credit Card</option><option>Wire Transfer</option><option>Cash</option>
                  </select>
                </label>
                <label>Currency<br/>
                  <select value={form.currency} onChange={e => set('currency', e.target.value)}>
                    <option>CAD</option><option>USD</option>
                  </select>
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Our Account # (with this vendor)<br/><input value={form.ourAccountNumber} onChange={e => set('ourAccountNumber', e.target.value)} /></label>
                <label>Credit Limit ($)<br/><input type="number" value={form.creditLimit} onChange={e => set('creditLimit', e.target.value)} /></label>
              </div>

              <div style={{ fontWeight: 700, fontSize: 12, letterSpacing: '.04em', color: 'var(--text-dim)', marginBottom: 12 }}>EARLY PAYMENT DISCOUNT (OPTIONAL)</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Discount %<br/><input type="number" value={form.discountPct} onChange={e => set('discountPct', e.target.value)} placeholder="e.g. 2" /></label>
                <label>If Paid Within (days)<br/><input type="number" value={form.discountDays} onChange={e => set('discountDays', e.target.value)} placeholder="e.g. 10" /></label>
              </div>

              <label style={{ display: 'block', maxWidth: 320, marginBottom: 18 }}>Tax ID / Business Number<br/><input value={form.taxId} onChange={e => set('taxId', e.target.value)} /></label>

              <div style={{ fontWeight: 700, fontSize: 12, letterSpacing: '.04em', color: 'var(--text-dim)', marginBottom: 12 }}>DIRECT PAYMENT (ACH/EFT)</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14 }}>
                <label>Bank Name<br/><input value={form.bankName} onChange={e => set('bankName', e.target.value)} /></label>
                <label>Transit Number<br/><input value={form.transitNumber} onChange={e => set('transitNumber', e.target.value)} /></label>
                <label>Institution Number<br/><input value={form.institutionNumber} onChange={e => set('institutionNumber', e.target.value)} /></label>
                <label>Account Number<br/><input value={form.bankAccount} onChange={e => set('bankAccount', e.target.value)} /></label>
              </div>
            </>
          )}

          {tab === 2 && (
            !savedId ? <div className="empty">Save the vendor first to track document checklist items.</div>
            : checklist === null ? <div className="empty">Loading...</div>
            : (
              <>
                <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 14 }}>
                  Check a document once it's on file and set its expiry where relevant. This list is specific to vendors.
                </div>
                {checklist.length === 0 && <div className="empty">No document types yet — add W9/Tax Form, Insurance Certificate, or a Signed Vendor Agreement below.</div>}
                {checklist.map(item => (
                  <div className="checklist-row" key={item.doc_type_id}>
                    <input type="checkbox" checked={!!item.is_checked} style={{ width: 'auto' }} onChange={e => toggleChecklistItem(item, e.target.checked)} />
                    <div style={{ flex: 1 }}>{item.name}</div>
                    {!!item.is_required && <span className="checklist-req-badge">Required</span>}
                    <span style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 700 }}>EXPIRY</span>
                    <input type="date" value={item.expiry_date || ''} style={{ width: 150 }} onChange={e => setChecklistExpiry(item, e.target.value)} />
                  </div>
                ))}
                <button type="button" onClick={addDocType} style={{ marginTop: 14 }}>+ Add document type</button>
              </>
            )
          )}

          {error && <p style={{ color: '#c4364b', fontSize: 13, margin: '16px 0 0' }}>{error}</p>}
        </div>

        <div className="customer-modal-footer">
          <div className="modal-actions" style={{ margin: 0 }}>
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="button" onClick={reset}>Reset</button>
            <button type="button" className="primary-btn" onClick={saveAndNext} disabled={saving || tab === TABS.length - 1}>Save &amp; Next</button>
            <button type="button" className="primary-btn" onClick={saveAndClose} disabled={saving}>
              {saving ? 'Saving...' : vendor ? 'Save changes' : 'Create vendor'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
